import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../supabase'
import Navbar from '../components/Navbar'
import { dbGetProfile, dbGetRigs, dbGetDropzones, dbGetQuals, dbGetAircraft, dbGetDocuments, dbSetDocuments, dbGetAvatar, dbSetAvatar } from '../db'
import { getUser } from '../getUser'
import { useProfile } from '../useProfile'

export default function Profile() {
  const { t } = useTranslation()
  const { canExport, canImport, canStats } = useProfile()
  const [profileBase, setProfileBase] = useState(null)
  const [preview, setPreview]         = useState(null)
  const [uploading, setUploading]     = useState(false)
  const [dropzones, setDropzones]     = useState([])
  const [docs, setDocs]               = useState([])
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [newDz, setNewDz]             = useState('')
  const [aircraft, setAircraft]       = useState([])
  const [newAc, setNewAc]             = useState('')
  const [msgs, setMsgs]               = useState({})
  const [saving, setSaving]           = useState({})
  const [rigs, setRigs]               = useState([])
  const [showAddRig, setShowAddRig]   = useState(false)
  const [editingRig, setEditingRig]   = useState(null)
  const [newRig, setNewRig]           = useState({ name:'', main:'', reserve:'', container:'', aad:'', reserve_expiry:'' })
  const [savingRig, setSavingRig]     = useState(false)
  const fileRef  = useRef()
  const docRef   = useRef()
  const navigate = useNavigate()

  const load = useCallback(async () => {
    if (!navigator.onLine) {
      const [prof, rigList, dz, ac, docList] = await Promise.all([
        dbGetProfile(), dbGetRigs(), dbGetDropzones(), dbGetAircraft(), dbGetDocuments()
      ])
      if (prof) setProfileBase({ ...prof, email: prof.email || '', uid: prof.id })
      const avatarData = await dbGetAvatar()
      if (avatarData) setPreview(avatarData)
      else if (prof?.avatar_url) setPreview(prof.avatar_url)
      setRigs(rigList || [])
      setDropzones(dz || [])
      setAircraft(ac || [])
      setDocs(docList || [])
      return
    }
    const user = await getUser()
    const [{ data: prof }, { data: dz }, { data: docList }, { data: rigList }, { data: acList }] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', user.id).single(),
      supabase.from('dropzones').select('*').eq('user_id', user.id).order('name'),
      supabase.storage.from('documents').list(user.id, { sortBy: { column: 'created_at', order: 'desc' } }),
      supabase.from('rigs').select('*').eq('user_id', user.id).order('created_at'),
      supabase.from('aircraft').select('*').eq('user_id', user.id).order('name'),
    ])
    setProfileBase({ ...prof, email: user.email, uid: user.id })
    if (prof?.avatar_url) {
      setPreview(prof.avatar_url + '?t=' + Date.now())
      // Zapisz avatar do IDB jako base64
      try {
        const res = await fetch(prof.avatar_url)
        const blob = await res.blob()
        const reader = new FileReader()
        reader.onloadend = () => dbSetAvatar(reader.result)
        reader.readAsDataURL(blob)
      } catch {}
    }
    setDropzones(dz || [])
    setDocs(docList || [])
    await dbSetDocuments(docList || [])
    setRigs(rigList || [])
    setAircraft(acList || [])
  }, [])

  useEffect(() => { load() }, [load])

  const showMsg = (key, text) => {
    setMsgs(m => ({ ...m, [key]: text }))
    setTimeout(() => setMsgs(m => ({ ...m, [key]: null })), 2500)
  }

  const pickAvatar = async (e) => {
    const file = e.target.files[0]
    if (!file || !profileBase) return
    setPreview(URL.createObjectURL(file))
    setUploading(true)
    try {
      const ext = file.name.split('.').pop().toLowerCase()
      const path = `${profileBase.uid}/avatar.${ext}`
      await supabase.storage.from('avatars').remove([path])
      const { error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type, cacheControl: '0' })
      if (!error) {
        const { data } = supabase.storage.from('avatars').getPublicUrl(path)
        await supabase.from('profiles').update({ avatar_url: data.publicUrl }).eq('id', profileBase.uid)
        setPreview(data.publicUrl + '?t=' + Date.now())
        showMsg('avatar', t('profile.saved'))
      }
    } catch (e) { showMsg('avatar', t('common.error')) }
    setUploading(false)
  }

  const uploadDoc = async (e) => {
    const file = e.target.files[0]
    if (!file || !profileBase) return
    setUploadingDoc(true)
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    const path = `${profileBase.uid}/${Date.now()}_${safeName}`
    const { error } = await supabase.storage.from('documents').upload(path, file, { contentType: file.type })
    if (!error) {
      showMsg('docs', t('profile.saved'))
      const { data: docList } = await supabase.storage.from('documents').list(profileBase.uid, { sortBy: { column: 'created_at', order: 'desc' } })
      setDocs(docList || [])
    await dbSetDocuments(docList || [])
    } else {
      showMsg('docs', t('common.error'))
    }
    setUploadingDoc(false)
    e.target.value = ''
  }

  const downloadDoc = async (name) => {
    const { data } = await supabase.storage.from('documents').createSignedUrl(`${profileBase.uid}/${name}`, 300)
    if (data?.signedUrl) window.location.href = data.signedUrl
  }

  const deleteDoc = async (name) => {
    await supabase.storage.from('documents').remove([`${profileBase.uid}/${name}`])
    setDocs(d => d.filter(x => x.name !== name))
  }

  const addDropzone = async () => {
    if (!newDz.trim()) return
    const user = await getUser()
    const { data } = await supabase.from('dropzones').insert({ user_id: user.id, name: newDz.trim() }).select().single()
    if (data) setDropzones(dz => [...dz, data].sort((a, b) => a.name.localeCompare(b.name)))
    setNewDz('')
  }

  const deleteDz = async (id) => {
    await supabase.from('dropzones').delete().eq('id', id)
    setDropzones(dz => dz.filter(d => d.id !== id))
  }

  const DEFAULT_AIRCRAFT = [
    'Cessna 182', 'Cessna 206', 'Cessna 208 Caravan', 'Cessna 208B Grand Caravan',
    'Pilatus PC-6 Porter', 'Antonov An-2', 'Antonov An-28', 'AN-28 Bryza',
    'Let L-410 Turbolet', 'PAC 750 XL', 'de Havilland DHC-6 Twin Otter',
    'Beechcraft King Air', 'CASA C-295', 'Mi-8', 'Mi-2', 'AS350',
  ]

  const addAircraft = async () => {
    if (!newAc.trim()) return
    const user = await getUser()
    const { data } = await supabase.from('aircraft').insert({ user_id: user.id, name: newAc.trim() }).select().single()
    if (data) setAircraft(ac => [...ac, data].sort((a, b) => a.name.localeCompare(b.name)))
    setNewAc('')
  }

  const addDefaultAircraft = async (name) => {
    if (aircraft.some(a => a.name === name)) return
    const user = await getUser()
    const { data } = await supabase.from('aircraft').insert({ user_id: user.id, name }).select().single()
    if (data) setAircraft(ac => [...ac, data].sort((a, b) => a.name.localeCompare(b.name)))
  }

  const deleteAircraft = async (id) => {
    await supabase.from('aircraft').delete().eq('id', id)
    setAircraft(ac => ac.filter(a => a.id !== id))
  }

  const saveRig = async () => {
    if (!newRig.name.trim()) return
    setSavingRig(true)
    const user = await getUser()
    const { data } = await supabase.from('rigs').insert({
      user_id: user.id,
      name: newRig.name.trim(),
      main: newRig.main.trim() || null,
      reserve: newRig.reserve.trim() || null,
      container: newRig.container.trim() || null,
      aad: newRig.aad.trim() || null,
      reserve_expiry: newRig.reserve_expiry || null,
    }).select().single()
    if (data) {
      setRigs(r => [...r, data])
      setNewRig({ name:'', main:'', reserve:'', container:'', aad:'', reserve_expiry:'' })
      setShowAddRig(false)
    }
    setSavingRig(false)
  }

  const updateRig = async () => {
    if (!editingRig) return
    setSavingRig(true)
    const { data } = await supabase.from('rigs').update({
      name: editingRig.name.trim(),
      main: editingRig.main?.trim() || null,
      reserve: editingRig.reserve?.trim() || null,
      container: editingRig.container?.trim() || null,
      aad: editingRig.aad?.trim() || null,
      reserve_expiry: editingRig.reserve_expiry || null,
    }).eq('id', editingRig.id).select().single()
    if (data) setRigs(r => r.map(x => x.id === data.id ? data : x))
    setEditingRig(null)
    setSavingRig(false)
  }

  const deleteRig = async (id) => {
    await supabase.from('rigs').delete().eq('id', id)
    setRigs(r => r.filter(x => x.id !== id))
  }

  const rigStatus = (expiry) => {
    if (!expiry) return null
    const days = Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24))
    if (days < 0)   return { color:'var(--danger)', label: t('profile.rig_expired', { days: Math.abs(days) }) }
    if (days <= 30) return { color:'#FBBF24',       label: t('profile.rig_expiring', { days }) }
    return              { color:'var(--success)',   label: t('profile.rig_valid', { days }) }
  }

  const fileIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase()
    if (['jpg','jpeg','png','webp','gif'].includes(ext)) return '🖼'
    if (ext === 'pdf') return '📄'
    return '📎'
  }

  const fmtSize = (bytes) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024*1024) return `${(bytes/1024).toFixed(0)} KB`
    return `${(bytes/1024/1024).toFixed(1)} MB`
  }

  const cleanName = (name) => name.replace(/^\d+_/, '')

  if (!profileBase) return <div><Navbar /><p style={{ textAlign:'center', padding:'4rem', color:'var(--muted)' }}>{t('profile.loading')}</p></div>

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth:520, margin:'0 auto', padding:'1.5rem 1rem' }}>

        {/* Avatar */}
        <div style={{ background:'var(--bg2)', border:'1px solid var(--border2)', borderRadius:'var(--r2)', padding:'2rem', textAlign:'center', marginBottom:'1rem' }}>
          <div onClick={() => !uploading && fileRef.current.click()} style={{ position:'relative', display:'inline-block', cursor: uploading ? 'wait' : 'pointer', marginBottom:'1rem' }}>
            <div style={{ width:90, height:90, borderRadius:'50%', background:'var(--accent)', border:'3px solid rgba(108,99,255,0.4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2rem', fontWeight:700, overflow:'hidden', margin:'0 auto', position:'relative' }}>
              {preview ? <img src={preview} alt="avatar" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : `${profileBase.name?.[0]||''}${profileBase.surname?.[0]||''}`}
              {uploading && <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', color:'#fff' }}>...</div>}
            </div>
            <div style={{ position:'absolute', bottom:0, right:0, width:26, height:26, borderRadius:'50%', background: uploading ? 'var(--muted)' : 'var(--accent)', border:'2px solid var(--bg2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:'#fff' }}>{uploading ? '⏳' : '✏'}</div>
          </div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickAvatar} style={{ display:'none' }} />
          {msgs['avatar'] && <div style={{ fontSize:'0.82rem', color:'var(--success)', marginBottom:'0.5rem' }}>{msgs['avatar']}</div>}
          <div style={{ fontFamily:'var(--head)', fontSize:'1.3rem', fontWeight:800 }}>{profileBase.name} {profileBase.surname}</div>
          <div style={{ color:'var(--muted)', fontSize:'0.72rem', marginTop:6 }}>{t('profile.click_to_change')}</div>
        </div>

        {/* Dokumenty */}
        <div className="card" style={{ marginBottom:'1rem' }}>
          <h3 style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, marginBottom:'0.25rem' }}>{t('profile.docs_title')}</h3>
          <p style={{ color:'var(--muted)', fontSize:'0.82rem', marginBottom:'1.25rem' }}>{t('profile.docs_desc')}</p>
          {docs.length === 0 && (
            <div style={{ textAlign:'center', padding:'1.5rem', color:'var(--muted)', fontSize:'0.85rem', background:'var(--bg3)', borderRadius:'var(--r)', marginBottom:'1rem' }}>
              {t('profile.docs_empty')}
            </div>
          )}
          {docs.map(doc => (
            <div key={doc.name} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.75rem 1rem', background:'var(--bg3)', borderRadius:'var(--r)', marginBottom:'0.5rem', border:'1px solid var(--border)' }}>
              <span style={{ fontSize:22, flexShrink:0 }}>{fileIcon(doc.name)}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:'0.88rem', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cleanName(doc.name)}</div>
                {doc.metadata?.size && <div style={{ fontSize:'0.72rem', color:'var(--muted)', fontFamily:'var(--mono)', marginTop:2 }}>{fmtSize(doc.metadata.size)}</div>}
              </div>
              <div style={{ display:'flex', gap:'0.4rem', flexShrink:0 }}>
                {navigator.onLine ? (
                  <button onClick={() => downloadDoc(doc.name)} style={{ background:'transparent', border:'1px solid var(--border2)', borderRadius:7, color:'var(--accent2)', cursor:'pointer', fontSize:'0.75rem', padding:'0.3rem 0.6rem', fontFamily:'var(--font)' }}>{t('profile.docs_download')}</button>
                ) : (
                  <span style={{ fontSize:'0.7rem', color:'var(--muted)', padding:'0.3rem 0.6rem' }}>📵 Offline</span>
                )}
                {navigator.onLine && (
                  <button onClick={() => deleteDoc(doc.name)} style={{ background:'transparent', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1rem', padding:'0.2rem 0.4rem', borderRadius:5 }}
                    onMouseEnter={e => e.target.style.color='var(--danger)'}
                    onMouseLeave={e => e.target.style.color='var(--muted)'}>✕</button>
                )}
              </div>
            </div>
          ))}
          {msgs['docs'] && <p style={{ color:'var(--success)', fontSize:'0.85rem', marginBottom:'0.5rem' }}>{msgs['docs']}</p>}
          <button
            onClick={() => docRef.current.click()}
            disabled={uploadingDoc}
            style={{ width:'100%', padding:'0.65rem', background:'var(--bg3)', border:'2px dashed var(--border2)', borderRadius:'var(--r)', color: uploadingDoc ? 'var(--muted)' : 'var(--accent2)', fontFamily:'var(--font)', fontSize:'0.88rem', fontWeight:500, cursor: uploadingDoc ? 'wait' : 'pointer', transition:'all 0.2s', marginTop:'0.5rem' }}
            onMouseEnter={e => { if (!uploadingDoc) e.currentTarget.style.borderColor='var(--accent)' }}
            onMouseLeave={e => e.currentTarget.style.borderColor='var(--border2)'}
          >
            {uploadingDoc ? t('profile.docs_uploading') : t('profile.docs_add')}
          </button>
          <input ref={docRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={uploadDoc} style={{ display:'none' }} />
        </div>

        <PersonalSection profileBase={profileBase} saving={saving} setSaving={setSaving} msgs={msgs} showMsg={showMsg} t={t} />
        <InsuranceSection profileBase={profileBase} saving={saving} setSaving={setSaving} msgs={msgs} showMsg={showMsg} t={t} />
        <MedicalSection profileBase={profileBase} saving={saving} setSaving={setSaving} msgs={msgs} showMsg={showMsg} t={t} />

        {/* Uprawnienia */}
        <Link to="/qualifications" style={{ textDecoration:'none', display:'block', marginBottom:'1rem' }}>
          <div className="card" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}
            onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor='var(--border2)'}
          >
            <div>
              <div style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, marginBottom:'0.15rem', color:'var(--text)' }}>{t('profile.qualifications')}</div>
              <div style={{ fontSize:'0.82rem', color:'var(--muted)' }}>{t('profile.qualifications_desc')}</div>
            </div>
            <span style={{ color:'var(--accent2)', fontSize:'1.2rem' }}>→</span>
          </div>
        </Link>

        {/* Komplety spadochronowe */}
        <div className="card" style={{ marginBottom:'1rem' }}>
          <h3 style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, marginBottom:'0.25rem' }}>{t('profile.rigs_title')}</h3>
          <p style={{ color:'var(--muted)', fontSize:'0.82rem', marginBottom:'1rem' }}>{t('profile.rigs_desc')}</p>
          {rigs.length === 0 && !showAddRig && (
            <div style={{ textAlign:'center', padding:'1.5rem', color:'var(--muted)', fontSize:'0.85rem', background:'var(--bg3)', borderRadius:'var(--r)', marginBottom:'1rem' }}>
              {t('profile.rigs_empty')}
            </div>
          )}
          {rigs.map(rig => {
            const status = rigStatus(rig.reserve_expiry)
            const isEditing = editingRig?.id === rig.id
            return (
              <div key={rig.id} style={{ background:'var(--bg3)', borderRadius:'var(--r)', padding:'1rem', marginBottom:'0.75rem', border:`1px solid ${isEditing ? 'var(--accent)' : 'var(--border)'}` }}>
                {isEditing ? (
                  <>
                    <div style={{ fontFamily:'var(--head)', fontSize:'0.9rem', fontWeight:800, marginBottom:'0.75rem', color:'var(--accent2)' }}>{t('profile.rigs_edit')}</div>
                    <div className="form-group"><label className="label">{t('profile.rigs_name')}</label><input className="input" value={editingRig.name} onChange={e => setEditingRig(r => ({ ...r, name: e.target.value }))} /></div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                      <div className="form-group"><label className="label">{t('profile.rigs_main')}</label><input className="input" value={editingRig.main || ''} onChange={e => setEditingRig(r => ({ ...r, main: e.target.value }))} /></div>
                      <div className="form-group"><label className="label">{t('profile.rigs_reserve')}</label><input className="input" value={editingRig.reserve || ''} onChange={e => setEditingRig(r => ({ ...r, reserve: e.target.value }))} /></div>
                      <div className="form-group"><label className="label">{t('profile.rigs_container')}</label><input className="input" value={editingRig.container || ''} onChange={e => setEditingRig(r => ({ ...r, container: e.target.value }))} /></div>
                      <div className="form-group"><label className="label">{t('profile.rigs_aad')}</label><input className="input" value={editingRig.aad || ''} onChange={e => setEditingRig(r => ({ ...r, aad: e.target.value }))} /></div>
                      <div className="form-group" style={{ gridColumn:'1 / -1' }}>
                        <label className="label">{t('profile.rigs_expiry')}</label>
                        <input className="input" type="date" value={editingRig.reserve_expiry || ''} onChange={e => setEditingRig(r => ({ ...r, reserve_expiry: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
                      <button className="btn ghost" style={{ flex:1 }} onClick={() => setEditingRig(null)}>{t('common.cancel')}</button>
                      <button className="btn" style={{ flex:1 }} onClick={updateRig} disabled={savingRig || !editingRig.name.trim()}>{savingRig ? t('profile.rigs_saving') : t('profile.rigs_save_changes')}</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem' }}>
                      <div style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, color:'var(--accent2)' }}>{rig.name}</div>
                      <div style={{ display:'flex', gap:'0.4rem' }}>
                        <button onClick={() => setEditingRig({ ...rig })}
                          style={{ background:'transparent', border:'1px solid var(--border2)', borderRadius:7, color:'var(--muted)', cursor:'pointer', fontSize:'0.75rem', padding:'0.3rem 0.6rem', fontFamily:'var(--font)' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent2)' }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.color='var(--muted)' }}
                        >{t('profile.rigs_edit_btn')}</button>
                        <button onClick={() => deleteRig(rig.id)} style={{ background:'transparent', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1rem' }}
                          onMouseEnter={e => e.target.style.color='var(--danger)'}
                          onMouseLeave={e => e.target.style.color='var(--muted)'}>✕</button>
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', fontSize:'0.82rem' }}>
                      {rig.main && <div style={{ background:'var(--bg2)', borderRadius:8, padding:'0.5rem 0.75rem' }}><div style={{ color:'var(--muted)', fontSize:'0.65rem', fontFamily:'var(--mono)', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>{t('profile.rigs_main')}</div><div style={{ fontWeight:600 }}>{rig.main}</div></div>}
                      {rig.reserve && <div style={{ background:'var(--bg2)', borderRadius:8, padding:'0.5rem 0.75rem' }}><div style={{ color:'var(--muted)', fontSize:'0.65rem', fontFamily:'var(--mono)', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>{t('profile.rigs_reserve')}</div><div style={{ fontWeight:600 }}>{rig.reserve}</div></div>}
                      {rig.container && <div style={{ background:'var(--bg2)', borderRadius:8, padding:'0.5rem 0.75rem' }}><div style={{ color:'var(--muted)', fontSize:'0.65rem', fontFamily:'var(--mono)', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>{t('profile.rigs_container')}</div><div style={{ fontWeight:600 }}>{rig.container}</div></div>}
                      {rig.aad && <div style={{ background:'var(--bg2)', borderRadius:8, padding:'0.5rem 0.75rem' }}><div style={{ color:'var(--muted)', fontSize:'0.65rem', fontFamily:'var(--mono)', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>{t('profile.rigs_aad')}</div><div style={{ fontWeight:600 }}>{rig.aad}</div></div>}
                      {rig.reserve_expiry && <div style={{ background:'var(--bg2)', borderRadius:8, padding:'0.5rem 0.75rem', gridColumn:'1 / -1' }}><div style={{ color:'var(--muted)', fontSize:'0.65rem', fontFamily:'var(--mono)', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>{t('profile.rigs_expiry')}</div><div style={{ fontWeight:600 }}>{new Date(rig.reserve_expiry).toLocaleDateString('pl-PL')}</div></div>}
                    </div>
                    {status && <div style={{ marginTop:'0.75rem', padding:'0.6rem 0.9rem', borderRadius:'var(--r)', fontSize:'0.82rem', fontWeight:600, color:status.color, background:'rgba(255,255,255,0.04)', border:`1px solid ${status.color}` }}>{status.label}</div>}
                  </>
                )}
              </div>
            )
          })}
          {showAddRig && (
            <div style={{ background:'var(--bg3)', borderRadius:'var(--r)', padding:'1rem', marginBottom:'0.75rem', border:'1px solid var(--border2)' }}>
              <div style={{ fontFamily:'var(--head)', fontSize:'0.9rem', fontWeight:800, marginBottom:'0.75rem', color:'var(--accent2)' }}>{t('profile.rigs_new')}</div>
              <div className="form-group"><label className="label">{t('profile.rigs_name')}</label><input className="input" placeholder="np. Mój główny zestaw" value={newRig.name} onChange={e => setNewRig(r => ({ ...r, name: e.target.value }))} /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div className="form-group"><label className="label">{t('profile.rigs_main')}</label><input className="input" placeholder="np. Pilot 168" value={newRig.main} onChange={e => setNewRig(r => ({ ...r, main: e.target.value }))} /></div>
                <div className="form-group"><label className="label">{t('profile.rigs_reserve')}</label><input className="input" placeholder="np. Nano 160" value={newRig.reserve} onChange={e => setNewRig(r => ({ ...r, reserve: e.target.value }))} /></div>
                <div className="form-group"><label className="label">{t('profile.rigs_container')}</label><input className="input" placeholder="np. Javelin Odyssey" value={newRig.container} onChange={e => setNewRig(r => ({ ...r, container: e.target.value }))} /></div>
                <div className="form-group"><label className="label">{t('profile.rigs_aad')}</label><input className="input" placeholder="np. Cypres 2" value={newRig.aad} onChange={e => setNewRig(r => ({ ...r, aad: e.target.value }))} /></div>
                <div className="form-group" style={{ gridColumn:'1 / -1' }}><label className="label">{t('profile.rigs_expiry')}</label><input className="input" type="date" value={newRig.reserve_expiry} onChange={e => setNewRig(r => ({ ...r, reserve_expiry: e.target.value }))} /></div>
              </div>
              <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
                <button className="btn ghost" style={{ flex:1 }} onClick={() => { setShowAddRig(false); setNewRig({ name:'', main:'', reserve:'', container:'', aad:'', reserve_expiry:'' }) }}>{t('common.cancel')}</button>
                <button className="btn" style={{ flex:1 }} onClick={saveRig} disabled={savingRig || !newRig.name.trim()}>{savingRig ? t('profile.rigs_saving') : t('profile.rigs_save')}</button>
              </div>
            </div>
          )}
          {!showAddRig && !editingRig && (
            <button onClick={() => setShowAddRig(true)}
              style={{ width:'100%', padding:'0.65rem', background:'var(--bg3)', border:'2px dashed var(--border2)', borderRadius:'var(--r)', color:'var(--accent2)', fontFamily:'var(--font)', fontSize:'0.88rem', fontWeight:500, cursor:'pointer', transition:'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border2)'}
            >{t('profile.rigs_add')}</button>
          )}
        </div>

        {/* Moje samoloty */}
        <div className="card" style={{ marginBottom:'1rem' }}>
          <h3 style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, marginBottom:'0.25rem' }}>✈️ Moje samoloty</h3>
          <p style={{ color:'var(--muted)', fontSize:'0.82rem', marginBottom:'1rem' }}>Lista dostępna przy dodawaniu skoku</p>
          {aircraft.length === 0 && <div style={{ textAlign:'center', padding:'1rem', color:'var(--muted)', fontSize:'0.85rem', background:'var(--bg3)', borderRadius:'var(--r)', marginBottom:'1rem' }}>Brak samolotów</div>}
          {aircraft.length > 0 && (
            <div style={{ marginBottom:'1rem' }}>
              <details style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--r)', overflow:'hidden' }}>
                <summary style={{ padding:'0.7rem 1rem', cursor:'pointer', fontSize:'0.88rem', fontWeight:500, listStyle:'none', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span>✈️ Twoje samoloty ({aircraft.length})</span>
                  <span style={{ color:'var(--muted)', fontSize:'0.75rem' }}>▼</span>
                </summary>
                <div style={{ borderTop:'1px solid var(--border)', padding:'0.5rem' }}>
                  {aircraft.map(ac => (
                    <div key={ac.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.5rem 0.75rem', borderRadius:'var(--r)', marginBottom:'0.25rem' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--bg2)'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <span style={{ fontSize:'0.85rem' }}>✈️ {ac.name}</span>
                      <button onClick={() => deleteAircraft(ac.id)} style={{ background:'transparent', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'0.9rem', padding:'0.1rem 0.4rem', borderRadius:4 }}
                        onMouseEnter={e => e.currentTarget.style.color='var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color='var(--muted)'}>✕</button>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
          <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
            <input className="input" placeholder="np. Cessna 182" value={newAc} onChange={e => setNewAc(e.target.value)} onKeyDown={e => e.key==='Enter' && addAircraft()} style={{ flex:1 }} />
            <button onClick={addAircraft} disabled={!newAc.trim()} className="btn" style={{ width:'auto', padding:'0 1.25rem' }}>+ Dodaj</button>
          </div>
          <div style={{ marginTop:'1rem' }}>
            <div style={{ fontSize:'0.72rem', color:'var(--muted)', fontFamily:'var(--mono)', textTransform:'uppercase', letterSpacing:1, marginBottom:'0.5rem' }}>Szybkie dodawanie</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
              {DEFAULT_AIRCRAFT.filter(d => !aircraft.some(a => a.name === d)).map(name => (
                <button key={name} onClick={() => addDefaultAircraft(name)}
                  style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:6, color:'var(--muted)', padding:'0.3rem 0.65rem', fontSize:'0.75rem', cursor:'pointer', fontFamily:'var(--font)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent2)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--muted)' }}>
                  + {name}
                </button>
              ))}
              {DEFAULT_AIRCRAFT.every(d => aircraft.some(a => a.name === d)) && (
                <span style={{ fontSize:'0.75rem', color:'var(--success)' }}>✓ Wszystkie domyślne samoloty dodane</span>
              )}
            </div>
          </div>
        </div>

        {/* Strefy zrzutu */}
        <div className="card" style={{ marginBottom:'1rem' }}>
          <h3 style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, marginBottom:'0.25rem' }}>{t('profile.dropzones_title')}</h3>
          <p style={{ color:'var(--muted)', fontSize:'0.82rem', marginBottom:'1rem' }}>{t('profile.dropzones_desc')}</p>
          {dropzones.length === 0 && <div style={{ textAlign:'center', padding:'1rem', color:'var(--muted)', fontSize:'0.85rem', background:'var(--bg3)', borderRadius:'var(--r)', marginBottom:'1rem' }}>{t('profile.dropzones_empty')}</div>}
          {dropzones.length > 0 && (
            <div style={{ marginBottom:'1rem' }}>
              <details style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--r)', overflow:'hidden' }}>
                <summary style={{ padding:'0.7rem 1rem', cursor:'pointer', fontSize:'0.88rem', fontWeight:500, listStyle:'none', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span>📍 Twoje strefy zrzutu ({dropzones.length})</span>
                  <span style={{ color:'var(--muted)', fontSize:'0.75rem' }}>▼</span>
                </summary>
                <div style={{ borderTop:'1px solid var(--border)', padding:'0.5rem' }}>
                  {dropzones.map(dz => (
                    <div key={dz.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.5rem 0.75rem', borderRadius:'var(--r)', marginBottom:'0.25rem' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--bg2)'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <span style={{ fontSize:'0.85rem' }}>📍 {dz.name}</span>
                      <button onClick={() => deleteDz(dz.id)} style={{ background:'transparent', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'0.9rem', padding:'0.1rem 0.4rem', borderRadius:4 }}
                        onMouseEnter={e => e.currentTarget.style.color='var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color='var(--muted)'}>✕</button>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
          <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
            <input className="input" placeholder={t('profile.dropzones_placeholder')} value={newDz} onChange={e => setNewDz(e.target.value)} onKeyDown={e => e.key==="Enter" && addDropzone()} style={{ flex:1 }} />
            <button onClick={addDropzone} disabled={!newDz.trim()} className="btn" style={{ width:'auto', padding:'0 1.25rem' }}>{t('profile.dropzones_add')}</button>
          </div>
        </div>

        {/* Przyciski nawigacyjne */}
        {canStats && (
          <Link to="/stats" style={{ textDecoration:'none', display:'block', marginBottom:'0.75rem' }}>
            <button className="btn ghost" style={{ width:'100%' }}>{t('profile.stats')}</button>
          </Link>
        )}
        <Link to="/edit-jumps" style={{ textDecoration:'none', display:'block', marginBottom:'0.75rem' }}>
          <button className="btn ghost" style={{ width:'100%' }}>{t('profile.edit_jumps')}</button>
        </Link>
        {canExport && (
          <Link to="/export" style={{ textDecoration:'none', display:'block', marginBottom:'0.75rem' }}>
            <button className="btn ghost" style={{ width:'100%' }}>Eksportuj skoki (PDF / CSV / Druk)</button>
          </Link>
        )}
        {canImport && (
          <Link to="/import" style={{ textDecoration:'none', display:'block', marginBottom:'0.75rem' }}>
            <button className="btn ghost" style={{ width:'100%' }}>{t('profile.import')}</button>
          </Link>
        )}

        <button className="btn danger" onClick={async () => { await supabase.auth.signOut(); navigate('/login') }} style={{ marginBottom:'2rem' }}>
          {t('profile.logout')}
        </button>

      </div>
    </div>
  )
}

function PersonalSection({ profileBase, saving, setSaving, msgs, showMsg, t }) {
  const [name, setName]       = useState(profileBase.name || '')
  const [surname, setSurname] = useState(profileBase.surname || '')
  const save = async (e) => {
    e.preventDefault()
    setSaving(s => ({ ...s, personal:true }))
    await supabase.from('profiles').update({ name, surname }).eq('id', profileBase.id)
    showMsg('personal', t('profile.saved'))
    setSaving(s => ({ ...s, personal:false }))
  }
  return (
    <div className="card" style={{ marginBottom:'1rem' }}>
      <h3 style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, marginBottom:'1.25rem' }}>{t('profile.personal_title')}</h3>
      <form onSubmit={save}>
        <div className="form-row">
          <div className="form-group"><label className="label">{t('profile.first_name')}</label><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Jan" /></div>
          <div className="form-group"><label className="label">{t('profile.last_name')}</label><input className="input" value={surname} onChange={e => setSurname(e.target.value)} placeholder="Kowalski" /></div>
        </div>
        <div className="form-group"><label className="label">{t('profile.email')}</label><input className="input" value={profileBase.email||''} disabled style={{ opacity:0.5, cursor:'not-allowed' }} readOnly /></div>
        {msgs['personal'] && <p style={{ color:'var(--success)', fontSize:'0.85rem', marginBottom:'0.5rem' }}>{msgs['personal']}</p>}
        <button className="btn" type="submit" disabled={saving['personal']}>{saving['personal'] ? t('profile.saving') : t('profile.save')}</button>
      </form>
    </div>
  )
}

function InsuranceSection({ profileBase, saving, setSaving, msgs, showMsg, t }) {
  const [company, setCompany] = useState(profileBase.insurance_company || '')
  const [number, setNumber]   = useState(profileBase.insurance_number || '')
  const [expiry, setExpiry]   = useState(profileBase.insurance_expiry || '')
  const save = async (e) => {
    e.preventDefault()
    setSaving(s => ({ ...s, insurance:true }))
    await supabase.from('profiles').update({ insurance_company: company||null, insurance_number: number||null, insurance_expiry: expiry||null }).eq('id', profileBase.id)
    showMsg('insurance', t('profile.saved'))
    setSaving(s => ({ ...s, insurance:false }))
  }
  return (
    <div className="card" style={{ marginBottom:'1rem' }}>
      <h3 style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, marginBottom:'1.25rem' }}>{t('profile.insurance_title')}</h3>
      <form onSubmit={save}>
        <div className="form-group"><label className="label">{t('profile.insurance_company')}</label><input className="input" value={company} onChange={e => setCompany(e.target.value)} placeholder="np. PZU, Warta..." /></div>
        <div className="form-row">
          <div className="form-group"><label className="label">{t('profile.insurance_number')}</label><input className="input" value={number} onChange={e => setNumber(e.target.value)} /></div>
          <div className="form-group"><label className="label">{t('profile.insurance_expiry')}</label><input className="input" type="date" value={expiry} onChange={e => setExpiry(e.target.value)} /></div>
        </div>
        {msgs['insurance'] && <p style={{ color:'var(--success)', fontSize:'0.85rem', marginBottom:'0.5rem' }}>{msgs['insurance']}</p>}
        <button className="btn" type="submit" disabled={saving['insurance']}>{saving['insurance'] ? t('profile.saving') : t('profile.save')}</button>
      </form>
    </div>
  )
}

function MedicalSection({ profileBase, saving, setSaving, msgs, showMsg, t }) {
  const [expiry, setExpiry] = useState(profileBase.medical_expiry || '')
  const save = async (e) => {
    e.preventDefault()
    setSaving(s => ({ ...s, medical:true }))
    await supabase.from('profiles').update({ medical_expiry: expiry||null }).eq('id', profileBase.id)
    showMsg('medical', t('profile.saved'))
    setSaving(s => ({ ...s, medical:false }))
  }
  return (
    <div className="card" style={{ marginBottom:'1rem' }}>
      <h3 style={{ fontFamily:'var(--head)', fontSize:'1rem', fontWeight:800, marginBottom:'1.25rem' }}>{t('profile.medical_title')}</h3>
      <form onSubmit={save}>
        <div className="form-group"><label className="label">{t('profile.medical_expiry')}</label><input className="input" type="date" value={expiry} onChange={e => setExpiry(e.target.value)} /></div>
        {msgs['medical'] && <p style={{ color:'var(--success)', fontSize:'0.85rem', marginBottom:'0.5rem' }}>{msgs['medical']}</p>}
        <button className="btn" type="submit" disabled={saving['medical']}>{saving['medical'] ? t('profile.saving') : t('profile.save')}</button>
      </form>
    </div>
  )
}